import React, { useState } from "react";
import { motion } from "framer-motion";

export const PILLAR = {
  E: { label: "Environment", color: "#00E676" },
  S: { label: "Social", color: "#4FC3F7" },
  G: { label: "Governance", color: "#FFB74D" },
};

const W = 560, H = 440, PADL = 56, PADB = 44, PADT = 16, PADR = 16;
const PW = W - PADL - PADR, PH = H - PADT - PADB;
const fx = (v) => PADL + (v / 100) * PW;
const fy = (v) => PADT + (1 - v / 100) * PH;

// Which quadrant a point falls in given thresholds
function quad(imp, fin, it, ft) {
  const im = imp >= it, fm = fin >= ft;
  if (im && fm) return "material_both";
  if (im) return "impact_material";
  if (fm) return "financial_material";
  return "below_threshold";
}

export default function DoubleMaterialityChart({ issues, methodology, pillarFilter, quadFilter, onSelect }) {
  const [hover, setHover] = useState(null);
  const it = methodology?.impact_threshold ?? 50;
  const ft = methodology?.financial_threshold ?? 50;
  const tx = fx(ft), ty = fy(it);

  const dimmed = (i) => {
    if (pillarFilter && pillarFilter !== "all" && i.pillar !== pillarFilter) return true;
    if (quadFilter && quadFilter !== "all" && quad(i.impact_score, i.financial_score, it, ft) !== quadFilter) return true;
    return false;
  };

  const quads = [
    { key: "material_both", x: tx, y: PADT, w: PADL + PW - tx, h: ty - PADT, fill: "rgba(215,255,0,0.08)", label: "Material — both", lx: PADL + PW - 6, ly: PADT + 14, anchor: "end" },
    { key: "impact_material", x: PADL, y: PADT, w: tx - PADL, h: ty - PADT, fill: "rgba(0,230,118,0.06)", label: "Impact-material", lx: PADL + 6, ly: PADT + 14, anchor: "start" },
    { key: "financial_material", x: tx, y: ty, w: PADL + PW - tx, h: PADT + PH - ty, fill: "rgba(79,195,247,0.06)", label: "Financially material", lx: PADL + PW - 6, ly: PADT + PH - 8, anchor: "end" },
    { key: "below_threshold", x: PADL, y: ty, w: tx - PADL, h: PADT + PH - ty, fill: "rgba(255,255,255,0.02)", label: "Below threshold", lx: PADL + 6, ly: PADT + PH - 8, anchor: "start" },
  ];

  return (
    <div className="relative" data-testid="double-materiality-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ maxHeight: 460 }}>
        {/* quadrant shading */}
        {quads.map((q) => (
          <g key={q.key}>
            <rect x={q.x} y={q.y} width={Math.max(0, q.w)} height={Math.max(0, q.h)} fill={q.fill}
              stroke={quadFilter === q.key ? "rgba(215,255,0,0.5)" : "transparent"} strokeWidth="1.5" />
            <text x={q.lx} y={q.ly} textAnchor={q.anchor} className="fill-muted-foreground"
              style={{ fontSize: 10, letterSpacing: 0.5, textTransform: "uppercase", opacity: 0.7 }}>{q.label}</text>
          </g>
        ))}

        {/* plot border */}
        <rect x={PADL} y={PADT} width={PW} height={PH} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        {/* threshold lines */}
        <line x1={tx} y1={PADT} x2={tx} y2={PADT + PH} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4 4" />
        <line x1={PADL} y1={ty} x2={PADL + PW} y2={ty} stroke="rgba(255,255,255,0.25)" strokeWidth="1" strokeDasharray="4 4" />

        {/* axis labels */}
        <text x={PADL + PW / 2} y={H - 10} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 11 }}>
          Financial materiality →
        </text>
        <text transform={`translate(14 ${PADT + PH / 2}) rotate(-90)`} textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: 11 }}>
          Impact materiality →
        </text>

        {/* dots */}
        {issues.map((i, idx) => {
          const cx = fx(i.financial_score), cy = fy(i.impact_score);
          const color = PILLAR[i.pillar]?.color || "#999";
          const isDim = dimmed(i);
          const isHover = hover?.id === i.id;
          return (
            <motion.g key={i.id}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: isDim ? 0.18 : 1, scale: 1 }}
              transition={{ delay: idx * 0.05, type: "spring", stiffness: 260, damping: 18 }}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHover({ ...i, cx, cy })}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect && onSelect(i)}
              data-testid={`dm-dot-${i.code}`}>
              {isHover && <circle cx={cx} cy={cy} r={12} fill="none" stroke={color} strokeWidth="1" opacity="0.5" />}
              <circle cx={cx} cy={cy} r={isHover ? 8 : 7}
                fill={i.missing_source ? "transparent" : color}
                stroke={color} strokeWidth={i.missing_source ? 2 : 1} />
              {i.evidence_confidence === "low" && (
                <circle cx={cx + 7} cy={cy - 7} r={3} fill="#FFB800" stroke="#000" strokeWidth="0.5" />
              )}
            </motion.g>
          );
        })}
      </svg>

      {hover && (
        <div className="absolute pointer-events-none rounded-sm border border-border bg-surface px-3 py-2 text-xs shadow-lg z-10"
          style={{ left: `${(hover.cx / W) * 100}%`, top: `${(hover.cy / H) * 100}%`, transform: "translate(-50%, -115%)", minWidth: 160 }}
          data-testid="dm-tooltip">
          <div className="font-medium flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full inline-block" style={{ background: PILLAR[hover.pillar]?.color }} />
            {hover.title}
          </div>
          <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
            Impact {hover.impact_score} · Financial {hover.financial_score}
          </div>
          <div className="text-[10px] text-muted-foreground">{hover.missing_source ? "Metric source missing" : "Metric linked"}{hover.evidence_confidence === "low" ? " · evidence gap" : ""}</div>
        </div>
      )}
    </div>
  );
}
